<script setup>
import { ref, reactive, computed, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  audienceList: { type: Object, default: null },
});

const emit = defineEmits(['imported']);

const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);
const POLL_INTERVAL_MS = 2000;

const state = reactive({
  step: 'filters', // filters | running | done
  query: '',
  sourceKey: '',
  defaultCountryCode: '55',
  preview: null,
  isPreviewing: false,
  isSubmitting: false,
  previewError: '',
  importRecord: null,
});

let pollTimer = null;

const stopPolling = () => {
  if (pollTimer) clearTimeout(pollTimer);
  pollTimer = null;
};

onBeforeUnmount(stopPolling);

const resetState = () => {
  stopPolling();
  Object.assign(state, {
    step: 'filters',
    query: '',
    sourceKey: '',
    defaultCountryCode: '55',
    preview: null,
    isPreviewing: false,
    isSubmitting: false,
    previewError: '',
    importRecord: null,
  });
};

// Only the filters that actually shape the scope travel to the server; the
// "has a phone or e-mail" predicate is always applied server-side.
const filters = computed(() => {
  const payload = {};
  if (state.query.trim()) payload.q = state.query.trim();
  if (state.sourceKey.trim()) payload.source_key = state.sourceKey.trim();
  return payload;
});

const reachableCount = computed(() => state.preview?.reachable_count ?? 0);
const canImport = computed(
  () => reachableCount.value > 0 && !state.isSubmitting
);

const handlePreview = async () => {
  state.isPreviewing = true;
  state.previewError = '';
  state.preview = null;
  try {
    state.preview = await store.dispatch(
      'audienceLists/registryPreview',
      filters.value
    );
  } catch (error) {
    state.previewError =
      error?.response?.data?.error ||
      t('CAMPAIGN.AUDIENCE.REGISTRY_IMPORT.PREVIEW_ERROR');
  } finally {
    state.isPreviewing = false;
  }
};

const pollImport = async () => {
  try {
    const record = await store.dispatch('audienceLists/pollImport', {
      id: props.audienceList.id,
      importId: state.importRecord.id,
    });
    state.importRecord = record;
    if (['completed', 'failed'].includes(record.status)) {
      state.step = 'done';
      emit('imported', record);
      return;
    }
  } catch (error) {
    // Transient polling failure: the job keeps running in Sidekiq, retry.
  }
  pollTimer = setTimeout(pollImport, POLL_INTERVAL_MS);
};

const startImport = async () => {
  state.isSubmitting = true;
  try {
    state.importRecord = await store.dispatch(
      'audienceLists/createRegistryImport',
      {
        id: props.audienceList.id,
        filters: filters.value,
        defaultCountryCode: state.defaultCountryCode,
      }
    );
    state.step = 'running';
    pollTimer = setTimeout(pollImport, POLL_INTERVAL_MS);
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('CAMPAIGN.AUDIENCE.REGISTRY_IMPORT.API.ERROR_MESSAGE')
    );
  } finally {
    state.isSubmitting = false;
  }
};

const handleConfirm = () => {
  if (state.step === 'filters') {
    startImport();
    return;
  }
  if (state.step === 'done') dialogRef.value?.close();
};

const confirmButtonLabel = computed(() =>
  state.step === 'done'
    ? t('CAMPAIGN.AUDIENCE.IMPORT.BUTTONS.CLOSE')
    : t('CAMPAIGN.AUDIENCE.REGISTRY_IMPORT.BUTTONS.IMPORT')
);

// Closing mid-import leaves the card counters stale, because polling is what
// refreshes them. Ask for the record once on the way out.
const handleClose = () => {
  const wasRunning = state.step === 'running';
  resetState();
  if (wasRunning && props.audienceList?.id) {
    store.dispatch('audienceLists/show', props.audienceList.id);
  }
};

const open = () => {
  resetState();
  dialogRef.value?.open();
};

defineExpose({ dialogRef, open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="2xl"
    overflow-y-auto
    :title="
      t('CAMPAIGN.AUDIENCE.REGISTRY_IMPORT.TITLE', { name: audienceList?.name })
    "
    :description="t('CAMPAIGN.AUDIENCE.REGISTRY_IMPORT.DESCRIPTION')"
    :confirm-button-label="confirmButtonLabel"
    :show-confirm-button="state.step === 'filters' || state.step === 'done'"
    :disable-confirm-button="state.step === 'filters' && !canImport"
    :is-loading="state.isSubmitting"
    @confirm="handleConfirm"
    @close="handleClose"
  >
    <!-- Step 1: filters + real count -->
    <div v-if="state.step === 'filters'" class="flex flex-col gap-4">
      <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div class="flex flex-col gap-1">
          <label
            for="registry-query"
            class="text-sm font-medium text-n-slate-12"
          >
            {{ t('CAMPAIGN.AUDIENCE.REGISTRY_IMPORT.QUERY.LABEL') }}
          </label>
          <Input
            id="registry-query"
            v-model="state.query"
            :placeholder="
              t('CAMPAIGN.AUDIENCE.REGISTRY_IMPORT.QUERY.PLACEHOLDER')
            "
          />
        </div>
        <div class="flex flex-col gap-1">
          <label
            for="registry-source"
            class="text-sm font-medium text-n-slate-12"
          >
            {{ t('CAMPAIGN.AUDIENCE.REGISTRY_IMPORT.SOURCE.LABEL') }}
          </label>
          <Input
            id="registry-source"
            v-model="state.sourceKey"
            :placeholder="
              t('CAMPAIGN.AUDIENCE.REGISTRY_IMPORT.SOURCE.PLACEHOLDER')
            "
          />
        </div>
      </div>

      <div class="flex flex-col gap-1 sm:max-w-xs">
        <label
          for="registry-country-code"
          class="text-sm font-medium text-n-slate-12"
        >
          {{ t('CAMPAIGN.AUDIENCE.IMPORT.COUNTRY_CODE.LABEL') }}
        </label>
        <Input
          id="registry-country-code"
          v-model="state.defaultCountryCode"
          :placeholder="t('CAMPAIGN.AUDIENCE.IMPORT.COUNTRY_CODE.PLACEHOLDER')"
        />
      </div>

      <div class="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="faded"
          color="slate"
          size="sm"
          icon="i-lucide-search"
          :label="t('CAMPAIGN.AUDIENCE.REGISTRY_IMPORT.BUTTONS.PREVIEW')"
          :disabled="state.isPreviewing"
          @click="handlePreview"
        />
        <span
          v-if="state.isPreviewing"
          class="flex items-center gap-1.5 text-xs text-n-slate-11"
        >
          <Spinner />
          {{ t('CAMPAIGN.AUDIENCE.REGISTRY_IMPORT.PREVIEW_LOADING') }}
        </span>
      </div>

      <p v-if="state.previewError" class="text-sm text-n-ruby-11">
        {{ state.previewError }}
      </p>

      <div
        v-else-if="state.preview"
        class="flex flex-col gap-1 p-3 rounded-lg bg-n-solid-2"
      >
        <span class="text-sm font-medium text-n-slate-12">
          {{
            t('CAMPAIGN.AUDIENCE.REGISTRY_IMPORT.REACHABLE', {
              count: state.preview.reachable_count,
            })
          }}
        </span>
        <span class="text-xs text-n-slate-11">
          {{
            t('CAMPAIGN.AUDIENCE.REGISTRY_IMPORT.OF_TOTAL', {
              total: state.preview.total_count,
            })
          }}
        </span>
      </div>

      <p class="text-xs text-n-slate-11">
        {{ t('CAMPAIGN.AUDIENCE.REGISTRY_IMPORT.INFO') }}
      </p>
    </div>

    <!-- Step 2: running -->
    <div
      v-else-if="state.step === 'running'"
      class="flex flex-col items-center gap-2 py-8 text-sm text-n-slate-11"
    >
      <Spinner />
      {{ t('CAMPAIGN.AUDIENCE.REGISTRY_IMPORT.RUNNING') }}
      <span v-if="state.importRecord" class="text-xs">
        {{ state.importRecord.processed_rows }} /
        {{ state.importRecord.total_rows }}
      </span>
    </div>

    <!-- Step 3: result -->
    <div v-else class="flex flex-col gap-2">
      <p class="text-sm text-n-slate-12">
        {{
          t('CAMPAIGN.AUDIENCE.REGISTRY_IMPORT.RESULT', {
            imported: state.importRecord?.imported_count ?? 0,
            updated: state.importRecord?.updated_count ?? 0,
            skipped: state.importRecord?.skipped_count ?? 0,
          })
        }}
      </p>
      <p
        v-if="state.importRecord?.error_message"
        class="text-sm text-n-ruby-11"
      >
        {{ state.importRecord.error_message }}
      </p>
    </div>
  </Dialog>
</template>
